import { useEffect, useId, useState } from 'react';
import { Save } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { STAFF_LEAVE_TYPE_OPTIONS, STAFF_LEAVE_LABEL } from '../constants';
import type { StaffLeaveBalance } from '@/types/staffLeave';
import {
  EMPTY_STAFF_LEAVE_FORM,
  staffLeaveSchema,
  type StaffLeaveFormValues,
  type StaffLeavePayload,
} from '../schemas/leave.schema';

type FieldErrors = Partial<Record<keyof StaffLeaveFormValues, string>>;

export interface StaffLeaveModalProps {
  isOpen: boolean;
  staffName: string;
  onClose: () => void;
  /** Resolves once the server has it. Rejects with a message to show in place. */
  onSave: (payload: StaffLeavePayload) => Promise<void>;
  /**
   * The person is applying for their own leave rather than the office
   * recording it. The same form and rules; only the wording speaks to them.
   */
  isOwn?: boolean;
  balances?: StaffLeaveBalance[];
}

/**
 * The office recording a leave application.
 *
 * Recorded *by* the office rather than applied for here: most Indian schools
 * still take leave on paper or by a word in the staff room, and the record has
 * to be able to catch up with that. A staff-facing application form is a
 * different screen, and the same endpoint serves it.
 *
 * The rules come from `staffLeaveSchema`, which mirrors the server's, rather
 * than from checks written out here ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â two copies of "the end cannot precede the
 * start" is how a form and an API end up disagreeing about what is valid.
 */
export function StaffLeaveModal({ isOpen, staffName, onClose, onSave, isOwn = false, balances }: StaffLeaveModalProps) {
  const formId = useId();

  const [values, setValues] = useState<StaffLeaveFormValues>(EMPTY_STAFF_LEAVE_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function set<K extends keyof StaffLeaveFormValues>(key: K, value: StaffLeaveFormValues[K]) {
    setValues((previous) => ({ ...previous, [key]: value }));
  }

  useEffect(() => {
    setValues(EMPTY_STAFF_LEAVE_FORM);
    setFieldErrors({});
    setFormError(null);
  }, [isOpen]);

  /**
   * Days follow the dates as they are picked, counting both ends.
   *
   * Still editable afterwards, because a half day and a three-day trip with one
   * working Saturday in it are both things the office knows and the dates do
   * not.
   */
  function pickStart(startDate: string) {
    const endDate = values.endDate && values.endDate >= startDate ? values.endDate : startDate;
    const isSingleDay = !values.endDate || values.endDate === startDate;
    setValues((previous) => ({
      ...previous,
      startDate,
      endDate,
      days: isSingleDay && previous.days === '0.5' ? '0.5' : String(daysBetween(startDate, endDate)),
    }));
  }

  function pickEnd(endDate: string) {
    const isSingleDay = !endDate || endDate === values.startDate;
    setValues((previous) => ({
      ...previous,
      endDate,
      days:
        isSingleDay && previous.days === '0.5'
          ? '0.5'
          : previous.startDate
            ? String(daysBetween(previous.startDate, endDate))
            : previous.days,
    }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    const parsed = staffLeaveSchema.safeParse({
      ...values,
      // An application with no end is a one-day application.
      endDate: values.endDate || values.startDate,
      // The input holds a string; an empty one must fail as "say how many",
      // not arrive as a zero the server then argues with.
      days: values.days === '' ? Number.NaN : Number(values.days),
    });

    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof StaffLeaveFormValues | undefined;
        if (field && !errors[field]) errors[field] = issue.message;
      }
      setFieldErrors(errors);
      setFormError(null);
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSaving(true);

    try {
      await onSave(parsed.data);
      onClose();
    } catch (caught) {
      setFormError(
        caught instanceof Error ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isOwn ? 'Apply for leave' : 'Record a leave application'}
      description={
        isOwn
          ? 'It goes to the school office to approve, and counts against your balance once approved.'
          : `It is counted against ${staffName}'s entitlement for this session once it is approved.`
      }
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            isLoading={isSaving}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {isOwn ? 'Send to the office' : 'Record it'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title={isOwn ? 'Could not send this request' : 'Could not record this leave'}>
            {formError}
          </Alert>
        )}

        <div>
          <Select
            label="Kind of leave"
            options={
              balances
                ? STAFF_LEAVE_TYPE_OPTIONS.map((opt) => {
                    const bal = balances.find((b: StaffLeaveBalance) => b.type === opt.value);
                    return bal
                      ? { value: opt.value, label: `${opt.label} ` }
                      : opt;
                  })
                : STAFF_LEAVE_TYPE_OPTIONS
            }
            value={values.type}
            error={fieldErrors.type}
            onChange={(event) => set('type', event.target.value as StaffLeaveFormValues['type'])}
          />
          {balances && (() => {
            const bal = balances.find((b: StaffLeaveBalance) => b.type === values.type);
            if (!bal) return null;
            const fullText = `${bal.remaining} of ${bal.allowed} ${STAFF_LEAVE_LABEL[bal.type]} days available (${bal.used} used)`;
            return (
              <p
                className="mt-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 cursor-default"
                title={fullText}
              >
                {bal.remaining} / {bal.allowed} {bal.allowed === 1 ? 'day' : 'days'} available
              </p>
            );
          })()}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DatePicker
            label="First day"
            value={values.startDate}
            error={fieldErrors.startDate}
            onChange={(event) => pickStart(event.target.value)}
          />
          <DatePicker
            label="Last day (optional)"
            placeholder="Same day"
            isClearable
            min={values.startDate || undefined}
            value={values.endDate}
            error={fieldErrors.endDate}
            onChange={(event) => pickEnd(event.target.value)}
          />
        </div>

        <Input
          label="Days"
          type="number"
          step="0.5"
          min="0.5"
          value={values.days}
          error={fieldErrors.days}
          hint="Prefilled as calendar days, counting both ends — weekends and holidays included. Use 0.5 for a half day."
          onChange={(event) => set('days', event.target.value)}
        />

        <Textarea
          label="Reason"
          value={values.reason}
          error={fieldErrors.reason}
          onChange={(event) => set('reason', event.target.value)}
          placeholder="Personal emergency, doctor's appointment, family function"
          hint="Held encrypted. Visible only to whoever may read this profile."
          rows={3}
        />
      </form>
    </Modal>
  );
}

/**
 * Calendar days, both ends included, so a one-day leave is one day rather than
 * nought. Weekends and holidays are counted — the field's hint says so, and the
 * figure stays editable for whoever knows the working days.
 */
function daysBetween(startDate: string, endDate: string): number {
  const start = Date.parse(startDate);
  const end = Date.parse(endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 1;
  return Math.round((end - start) / 86_400_000) + 1;
}
